import { useState, useImperativeHandle, forwardRef, useCallback, useEffect } from 'react';
import { Grid2 } from '@mui/material';
import { executeMVEL, validateMVELSyntax } from '@/src/utils/mvelUtils';
import {
  MVELEditorProps,
  EditorInstance,
  JSONSchema,
  MVELValidationResult,
  TestCase
} from '@/src/components/mvel-editor/types';
import MVELCodeEditor from '@/src/components/mvel-editor/MVELCodeEditor';

const MvelCodeEditorComponent = forwardRef<EditorInstance, MVELEditorProps>((props, ref) => {
  const {
    value = '',
    onChange,
    inputSchema,
    onValidation,
    height = '400px',
    readOnly = false
  } = props;

  const [mvelScript, setMvelScript] = useState(value);
  const [currentInputSchema] = useState<JSONSchema | undefined>(inputSchema);

  const handleScriptChange = useCallback(
    (newValue: string) => {
      setMvelScript(newValue);
      onChange?.(newValue);
    },
    [onChange]
  );

  const handleValidation = useCallback(
    (result: MVELValidationResult) => {
      onValidation?.(result);
    },
    [onValidation]
  );

  const validate = useCallback((): MVELValidationResult => {
    const result = validateMVELSyntax(mvelScript);
    return {
      isValid: result.isValid,
      errors: result.errors
    };
  }, [mvelScript]);

  const executeTest = useCallback(
    (testCase: TestCase): MVELValidationResult => {
      return executeMVEL(mvelScript, testCase.input);
    },
    [mvelScript]
  );

  // Expose methods through ref
  useImperativeHandle(
    ref,
    () => ({
      getValue: () => mvelScript,
      setValue: (newValue: string) => {
        setMvelScript(newValue);
        onChange?.(newValue);
      },
      validate,
      executeTest
    }),
    [mvelScript, onChange, validate, executeTest]
  );

  useEffect(() => {
    setMvelScript(value);
  }, [value]);

  return (
    <Grid2 container spacing={2} minWidth={500}>
      <Grid2 size={{ xs: 12 }}>
        <MVELCodeEditor
          value={mvelScript}
          onChange={handleScriptChange}
          inputSchema={currentInputSchema}
          onValidation={handleValidation}
          height={height}
          readOnly={readOnly}
        />
      </Grid2>
    </Grid2>
  );
});

MvelCodeEditorComponent.displayName = 'MvelCodeEditorComponent';

export default MvelCodeEditorComponent;
